import {
  DATE_PORTION,
  ENTITIES,
  POST_FIELDS,
  OPERATION,
  SAC_STATUS_FIELDS,
  SAC_SURVEYS_FIELDS,
  USERS_FIELDS,
} from '@/services/charts-service';
import {
  COMPONENT_TYPE_BASED,
  DATA_TYPE,
} from '@/store/modules/cards/constants';

import { generateRandomId } from '@/utils/math';
import { merge } from 'lodash';

export default {
  data() {
    return {
      tempFilter: {},
      fieldItems: [],
      groupingItems: [],
      operationItems: [],
      formValue: false,
      rules: {
        default: [
          (v) => Boolean(v) || this.$t('components.ComponentBuildModal.errorMessage.default'),
        ],
        detailsFilter: {
          title: [
            (v) => Boolean(v) || this.$t('components.ComponentBuildModal.errorMessage.default'),
            (v) => (v.length <= 50) || this.$t('components.ComponentBuildModal.errorMessage.details.title'),
          ],
          description: [
            (v) => Boolean(v) || this.$t('components.ComponentBuildModal.errorMessage.default'),
            (v) => (v.length <= 200) || this.$t('components.ComponentBuildModal.errorMessage.details.description'),
          ],
        },
        viewControl: {
          featureFlag: [
            (v) => (Boolean(v)) || this.$t('components.ComponentBuildModal.errorMessage.default'),
            (v) => !/\s/u.test(v) || this.$t('components.ComponentBuildModal.errorMessage.invalidValues'),
            (v) => v.indexOf(',') === -1 || this.$t('components.ComponentBuildModal.errorMessage.invalidValues'),
          ],
        },
      },
    };
  },
  watch: {
    'tempFilter.data.origin': {
      handler(newOrigin) {
        this.tempFilter.data.field = '';
        this.tempFilter.data.grouping = '';

        let fields;
        if (!newOrigin) {
          fields = merge({}, POST_FIELDS, USERS_FIELDS, SAC_STATUS_FIELDS, SAC_SURVEYS_FIELDS);
          this.fieldItems = this.getFieldItemsWithNoneOption(fields);
          this.groupingItems = this.getFieldItems(fields);
          return;
        }

        fields = this.getField[this.tempFilter.data.origin] || [];
        this.fieldItems = this.getFieldItemsWithNoneOption(fields);
        this.groupingItems = this.getFieldItems(fields);
      },
    },
    'tempFilter.data.field': {
      handler() {
        this.tempFilter.data.operation = '';
      },
    },
  },
  created() {
    this.tempFilter = this.getDefaultFilter();
  },
  computed: {
    getEntity() {
      return {
        [DATA_TYPE.LISTENING]: ENTITIES.POSTS,
        [DATA_TYPE.SAC]: ENTITIES.POSTS,
        [DATA_TYPE.SAC_STATUS]: ENTITIES.SAC_STATUS,
        [DATA_TYPE.COMPETITIVE]: ENTITIES.USERS,
        [DATA_TYPE.SAC_SURVEYS]: ENTITIES.SAC_SURVEYS,
      };
    },
    getField() {
      return {
        [DATA_TYPE.LISTENING]: POST_FIELDS,
        [DATA_TYPE.SAC]: POST_FIELDS,
        [DATA_TYPE.SAC_STATUS]: SAC_STATUS_FIELDS,
        [DATA_TYPE.COMPETITIVE]: USERS_FIELDS,
        [DATA_TYPE.SAC_SURVEYS]: SAC_SURVEYS_FIELDS,
      };
    },
    getOperation() {
      const allItems = [{
        text: this.$t('components.filters.OperationFilter.items.sum'),
        value: OPERATION.SUM,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.count'),
        value: OPERATION.COUNT,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.avg'),
        value: OPERATION.AVG,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.min'),
        value: OPERATION.MIN,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.max'),
        value: OPERATION.MAX,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.first'),
        value: OPERATION.FIRST,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.last'),
        value: OPERATION.LAST,
      },
      {
        text: this.$t('components.filters.OperationFilter.items.size'),
        value: OPERATION.SIZE,
      }];

      return allItems;
    },
  },
  methods: {
    getTextAndValueFromConst(fieldConst) {
      return Object.keys(fieldConst).map((fields) => ({
        text: fieldConst[fields],
        value: fieldConst[fields],
      }));
    },
    getFieldItemsWithNoneOption(fields) {
      return [{
        text: this.$t('components.filters.FieldFilter.label.none'),
        value: 'none',
      },
      ...this.getTextAndValueFromConst(fields),
      ];
    },
    getFieldItems(fields) {
      return this.getTextAndValueFromConst(fields);
    },
    getSeparatedString(string) {
      return string.split(',').map(string => string.trim()).filter(elem => elem);
    },
    getDefaultFilter() {
      return {
        data: {
          origin: '',
          field: '',
          operation: '',
          grouping: '',
          sorting: '',
        },
        areSensitiveData: false,
        details: {
          componentType: '',
          title: '',
          description: '',
          width: null,
          height: null,
        },
        featureFlag: '',
      };
    },
    transformFormDataIntoComponentObject() {
      const newComponent = {
        id: `custom_${generateRandomId()}`,
        width: this.tempFilter.details.width,
        height: this.tempFilter.details.height,
        component: COMPONENT_TYPE_BASED[this.tempFilter.details.componentType],
        componentType: this.tempFilter.details.componentType,
        visible: true,
        hideInLibrary: this.getSeparatedString(this.tempFilter.featureFlag),
        notSupportFilter: false,
        shareable: !this.tempFilter.areSensitiveData,
        dataType: this.tempFilter.data.origin,
        props: {
          title: this.tempFilter.details.title,
          originalTitle: this.tempFilter.details.title,
          isExpandedCard: false,
          description: this.tempFilter.details.description,
          fields: [this.tempFilter.data.field],
          operation: this.tempFilter.data.operation,
          grouping: [this.tempFilter.data.grouping],
          datePortion: DATE_PORTION.BY_YEAR,
          sorting: this.tempFilter.data.sorting,
          entity: this.getEntity[this.tempFilter.data.origin],
        },
      };

      if (this.tempFilter.data.field === 'none') {
        delete newComponent.props.fields;
      }

      return newComponent;
    },
    changeDisabledValueOfApplyButton(value) {
      const applyButton = document.querySelector('.ds-button--primary');

      if (!value) {
        applyButton.removeAttribute('disabled');
        return;
      }

      applyButton.setAttribute('disabled', value);
    },
    validateObjectValues(obj) {
      for (const key in obj) {
        if (typeof obj[key] === 'object') {
          if (!this.validateObjectValues(obj[key])) {
            return false;
          }
        } else if (obj[key] === null || obj[key] === '') {
          return false;
        }
      }
      return true;
    },
  },
};
